import React from 'react';
import { Database, CheckCircle2, Globe, Lock, AlertTriangle } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const LAST_UPDATED = 'April 28, 2026';
const CONTACT_EMAIL = 'dpo@builderslab.net';

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-zinc-800">{title}</h2>
      <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function ComplianceBadge({ label, status = 'compliant' }) {
  const colours = {
    compliant: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400',
    partial: 'bg-yellow-500/10 border-yellow-500/25 text-yellow-400',
    planned: 'bg-zinc-800 border-zinc-700 text-zinc-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${colours[status]}`}>
      {status === 'compliant' && <CheckCircle2 className="w-3 h-3" />}
      {label}
    </span>
  );
}

function ProcessorRow({ name, role, country, dpa }) {
  return (
    <tr className="border-b border-zinc-800/60">
      <td className="py-3 pr-4 text-white font-medium text-sm align-top">{name}</td>
      <td className="py-3 pr-4 text-zinc-400 text-sm align-top">{role}</td>
      <td className="py-3 pr-4 text-zinc-500 text-sm align-top">{country}</td>
      <td className="py-3 text-sm align-top">
        {dpa ? (
          <ComplianceBadge label="DPA Signed" status="compliant" />
        ) : (
          <ComplianceBadge label="Standard Terms" status="partial" />
        )}
      </td>
    </tr>
  );
}

export default function DataCompliance() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Data Compliance"
        description="BuildersLab's GDPR and data compliance information. Understand your rights and how we handle your personal data responsibly."
        ogUrl="https://builderslab.net/DataCompliance"
      />
      <div className="max-w-4xl mx-auto px-6">

        {/* Header */}
        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Data &amp; Compliance</h1>
          <p className="text-zinc-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-2xl">
            This page details how BuildersLab handles data from a compliance and regulatory perspective.
            It covers our commitments under GDPR, CCPA, and other applicable frameworks, our sub-processor
            register, data subject rights procedures, and our security posture.
          </p>
        </div>

        <div
         
         
         
        >

          {/* Compliance status cards */}
          <div className="grid sm:grid-cols-3 gap-4 mb-14">
            {[
              { icon: Globe, label: 'GDPR (EU/UK)', desc: 'General Data Protection Regulation', status: 'compliant' },
              { icon: Lock, label: 'CCPA (California)', desc: 'California Consumer Privacy Act', status: 'compliant' },
              { icon: Database, label: 'SOC 2 Type II', desc: 'Security & availability trust principles', status: 'planned' },
            ].map(({ icon: Icon, label, desc, status }) => (
              <div key={label} className={`p-5 rounded-2xl border ${status === 'compliant' ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-zinc-900 border-zinc-800'}`}>
                <Icon className={`w-5 h-5 mb-3 ${status === 'compliant' ? 'text-emerald-400' : 'text-zinc-500'}`} />
                <p className="text-white font-semibold text-sm">{label}</p>
                <p className="text-zinc-500 text-xs mt-0.5 mb-3">{desc}</p>
                <ComplianceBadge label={status === 'compliant' ? 'Compliant' : 'In Progress'} status={status} />
              </div>
            ))}
          </div>

          <Section title="1. Our Role as Data Controller">
            <p>
              BuildersLab acts as the <strong className="text-white">Data Controller</strong> for personal
              data collected from our users. This means we determine the purposes and means of processing
              your personal data. We work with a limited set of third-party processors (listed in Section 4)
              to deliver the Service.
            </p>
            <p>
              Our Data Protection contact can be reached at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </Section>

          <Section title="2. GDPR Compliance (EU & UK)">
            <p>
              If you are located in the European Economic Area (EEA) or United Kingdom, the General Data
              Protection Regulation (UK GDPR / EU GDPR 2016/679) applies to our processing of your personal
              data. Our commitments under GDPR include:
            </p>
            <ul className="list-none space-y-3 mt-3">
              {[
                ['Lawful basis for all processing', 'Every processing activity we undertake has a documented lawful basis: contract performance, legitimate interests, legal obligation, or consent. See our Privacy Policy for the basis applied to each data category.'],
                ['Data minimisation', 'We collect only the minimum personal data necessary for the stated purpose. We do not collect data speculatively.'],
                ['Purpose limitation', 'Data collected for one purpose (e.g., authentication) is not repurposed for another (e.g., marketing) without separate consent.'],
                ['Storage limitation', 'We enforce defined retention periods for each data category, after which data is automatically deleted or anonymised.'],
                ['Accuracy', 'You can update your personal data at any time from your account settings.'],
                ['Integrity and confidentiality', 'We apply industry-standard encryption, access controls, and security measures described in Section 6.'],
                ['Accountability', 'We maintain internal records of all processing activities (Article 30 Register) and conduct Data Protection Impact Assessments (DPIAs) for high-risk processing.'],
              ].map(([title, desc]) => (
                <li key={title} className="flex gap-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span><strong className="text-white">{title}:</strong> {desc}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-white font-semibold text-base mt-6 mb-2">International Transfers</h3>
            <p>
              Our infrastructure is hosted in the United States. For transfers of EEA/UK personal data
              to the US, we rely on:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Standard Contractual Clauses (SCCs) approved by the European Commission, incorporated into our Data Processing Agreements with sub-processors.',
                'The EU-US Data Privacy Framework (DPF) where our sub-processors are certified under it.',
                'UK IDTA (International Data Transfer Agreement) for UK-specific transfers.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="3. CCPA Compliance (California)">
            <p>
              If you are a California resident, the California Consumer Privacy Act (CCPA) as amended by
              the California Privacy Rights Act (CPRA) grants you the following rights:
            </p>
            <ul className="list-none space-y-3 mt-3">
              {[
                ['Right to Know', 'You have the right to know what personal information we collect, use, disclose, and sell (we do not sell personal information).'],
                ['Right to Delete', 'You may request deletion of personal information we have collected about you, subject to certain exceptions (e.g., completing a transaction, complying with a legal obligation).'],
                ['Right to Correct', 'You may request correction of inaccurate personal information we hold about you.'],
                ['Right to Opt-Out of Sale/Sharing', 'We do not sell or share personal information with third parties for cross-context behavioural advertising.'],
                ['Right to Limit Use of Sensitive Personal Information', 'We do not process sensitive personal information beyond what is necessary to provide the Service.'],
                ['Right to Non-Discrimination', 'We will not discriminate against you for exercising your CCPA rights.'],
              ].map(([right, desc]) => (
                <li key={right} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span><strong className="text-white">{right}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
              <strong className="text-white">Do Not Sell My Personal Information:</strong> We do not sell
              personal information. If this changes, we will update this page and provide an opt-out
              mechanism as required by law.
            </p>

            <h3 className="text-white font-semibold text-base mt-6 mb-2">Categories of Personal Information Collected</h3>
            <p>Under CCPA categories, we collect the following in the last 12 months:</p>
            <ul className="list-none space-y-2 mt-3">
              {[
                ['Identifiers', 'Name, email address, IP address, user ID.'],
                ['Commercial information', 'Subscription plan, credits balance, payment history.'],
                ['Internet or electronic network activity information', 'Pages visited, features used, session duration, browser/device information.'],
                ['Professional or employment-related information', 'Company name if provided during signup or investor enquiry.'],
                ['Inferences drawn from personal information', 'Usage patterns to personalise the product experience.'],
              ].map(([cat, desc]) => (
                <li key={cat} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span><strong className="text-white">{cat}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="4. Sub-Processor Register">
            <p>
              The following companies process personal data on our behalf as sub-processors. Each has
              agreed to appropriate data protection obligations.
            </p>
            <div className="overflow-x-auto rounded-xl border border-zinc-800 mt-5">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-zinc-700 bg-zinc-900/60">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Processor</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Role</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Country</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <ProcessorRow name="Hostinger International Ltd." role="Cloud hosting, database, file storage" country="Lithuania (EU)" dpa={true} />
                  <ProcessorRow name="Flutterwave Technology Solutions Ltd." role="Payment processing, subscription billing" country="Nigeria" dpa={true} />
                  <ProcessorRow name="NOWPayments (NOWPayments OÜ)" role="Cryptocurrency payment processing" country="Estonia (EU)" dpa={true} />
                  <ProcessorRow name="Anthropic, PBC" role="AI model API (prompt processing)" country="USA" dpa={true} />
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm">
              We will notify users of any material changes to our sub-processor list at least 14 days in
              advance by email, providing an opportunity to raise objections.
            </p>
          </Section>

          <Section title="5. Data Subject Rights — How to Exercise Them">
            <p>
              We take data subject rights seriously and have a clear process for handling requests:
            </p>

            <div className="mt-5 space-y-4">
              {[
                {
                  step: '1',
                  title: 'Submit your request',
                  desc: `Email ${CONTACT_EMAIL} with the subject line "Data Subject Request". Include your full name, the email address associated with your account, and clearly describe the right you wish to exercise.`,
                },
                {
                  step: '2',
                  title: 'Identity verification',
                  desc: 'We will verify your identity to protect your data from unauthorised access. This may involve confirming your email address or answering security questions.',
                },
                {
                  step: '3',
                  title: 'We respond within 30 days',
                  desc: 'We will fulfil your request or provide a reasoned explanation if we cannot. For complex requests we may extend by a further 60 days (with notification). All rights requests are free of charge.',
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-orange-500/15 border border-orange-500/25 flex items-center justify-center text-orange-400 font-bold text-sm shrink-0">
                    {step}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-1">{title}</p>
                    <p className="text-zinc-400 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-white font-semibold text-base mt-6 mb-2">Supervisory Authority</h3>
            <p>
              If you are located in the EEA or UK and believe we have not handled your data correctly,
              you have the right to lodge a complaint with your local supervisory authority. For UK
              residents, this is the Information Commissioner's Office (ICO) at{' '}
              <span className="text-orange-400">ico.org.uk</span>. For EU residents, contact your national
              Data Protection Authority.
            </p>
          </Section>

          <Section title="6. Security Measures">
            <p>
              We implement a layered security programme to protect personal data against unauthorised
              access, loss, destruction, or alteration:
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              {[
                { icon: Lock, title: 'Encryption in transit', desc: 'All communications between your browser and our servers are encrypted using TLS 1.2+ (HTTPS enforced everywhere).' },
                { icon: Database, title: 'Encryption at rest', desc: 'All stored data including database records and file storage is encrypted using AES-256.' },
                { icon: Lock, title: 'Password hashing', desc: 'User passwords are hashed with bcrypt using per-user salts. Plaintext passwords are never stored or logged.' },
                { icon: AlertTriangle, title: 'Access controls', desc: 'Production data access is restricted to authorised personnel via role-based access control (RBAC) and MFA.' },
                { icon: CheckCircle2, title: 'Vulnerability management', desc: 'We conduct periodic security reviews, dependency audits, and penetration tests.' },
                { icon: Globe, title: 'Incident response', desc: 'We have a documented incident response plan. In the event of a data breach we will notify affected users and relevant authorities within 72 hours as required by GDPR.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-orange-400" />
                    <p className="text-white font-semibold text-sm">{title}</p>
                  </div>
                  <p className="text-zinc-500 text-sm">{desc}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section title="7. Data Retention Summary">
            <p>
              The following table summarises our retention schedule. Full details are available in our{' '}
              <a href="/PrivacyPolicy" className="text-orange-400 hover:text-orange-300 transition-colors">
                Privacy Policy
              </a>.
            </p>
            <div className="overflow-x-auto rounded-xl border border-zinc-800 mt-5">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700 bg-zinc-900/60">
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Data Category</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Retention Period</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {[
                    ['Account & profile data', 'Until deletion + 30 days', 'Backup window for accidental deletions'],
                    ['Generated website content', 'Until deletion', 'User-owned content, stored on demand'],
                    ['AI prompts', 'Until account deletion', 'Linked to user content and session history'],
                    ['Payment & billing records', '7 years', 'Legal obligation (tax & accounting law)'],
                    ['Subscription history', '7 years', 'Legal obligation'],
                    ['Credits transaction log', 'Until deletion + 7 years', 'Financial record-keeping'],
                    ['Usage analytics', '24 months', 'Product improvement; anonymised after 6 months'],
                    ['Server & error logs', '90 days', 'Security monitoring and debugging'],
                    ['Session tokens', '30 days or logout', 'Authentication session management'],
                    ['Support communications', '3 years', 'Reference for ongoing support'],
                    ['Referral data', '12 months post-signup', 'Referral attribution window'],
                  ].map(([cat, period, reason]) => (
                    <tr key={cat} className="border-b border-zinc-800/60">
                      <td className="py-3 px-4 text-white text-sm">{cat}</td>
                      <td className="py-3 px-4 text-zinc-400 text-sm whitespace-nowrap">{period}</td>
                      <td className="py-3 px-4 text-zinc-500 text-sm">{reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="8. AI & Prompt Data">
            <p>
              BuildersLab processes AI prompts through Anthropic's Claude API. We want to be fully
              transparent about how this data is handled:
            </p>
            <ul className="list-none space-y-3 mt-3">
              {[
                ['Prompt transmission', 'Your prompt is sent to the Anthropic API over an encrypted connection to generate website code. Anthropic\'s API privacy policy governs how Anthropic handles API inputs.'],
                ['Prompt storage', 'We store your prompts in our database linked to your account so you have a history of what you\'ve built. You can delete individual projects (and their associated prompts) from your dashboard.'],
                ['Model training use', 'By default, we do not use your prompts to train our own AI models. If we ever wish to do so, we will obtain your explicit consent first and provide a clear opt-out mechanism.'],
                ['Anthropic\'s data use', 'Anthropic processes API requests under their own usage policies. By default, Anthropic does not use API data to train models unless explicitly opted into. See Anthropic\'s privacy policy for details.'],
              ].map(([title, desc]) => (
                <li key={title} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span><strong className="text-white">{title}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="9. Cookie Compliance">
            <p>
              We comply with the ePrivacy Directive (EU Cookie Law) and PECR (UK) by:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Only using strictly necessary cookies (authentication) required for core platform functionality.',
                'Providing clear information about cookie usage in our Privacy Policy.',
                'Honouring "Do Not Track" browser signals where practicable.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="10. Breach Notification Policy">
            <p>
              In the event of a personal data breach, BuildersLab will:
            </p>
            <ul className="list-none space-y-2 mt-3">
              {[
                'Assess the breach within 24 hours of detection.',
                'Notify the relevant supervisory authority (e.g., ICO in the UK) within 72 hours if the breach is likely to result in a risk to individuals\' rights and freedoms.',
                'Notify affected users without undue delay if the breach is likely to result in a high risk to their rights and freedoms.',
                'Provide details of: the nature of the breach, categories of data affected, approximate number of individuals affected, likely consequences, and measures taken or proposed.',
                'Maintain an internal breach register regardless of whether notification was required.',
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="11. Contact the Data Protection Team">
            <p>
              For all data and compliance enquiries — including Data Subject Access Requests, DPA
              negotiations, and security disclosures — please contact:
            </p>
            <div className="mt-4 p-5 bg-zinc-900 border border-zinc-800 rounded-xl space-y-2">
              <p className="text-white font-semibold">BuildersLab — Data Protection</p>
              <p>
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-2">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p className="text-zinc-500 text-sm">We respond to all data enquiries within 5 business days.</p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
